import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useState,
  useEffect,
} from 'react';
import { io } from 'socket.io-client';

import { IPocAiContext, PocAiProviderProps } from './interfaces';
import { IPocMessage, PocMessageType, EAudioPlaybackType } from '../interfaces';
import { visualizeAudio } from './avatarVisualizer';
import {
  POC_SCREENS,
  audioZoneScript,
  teleportPlaces,
  assistantSize,
  greetingsText,
  greetingsTextSecondary,
  actionsPromptOptionsExpression,
  actionsPromptOptionsTeleport,
  keys,
  celebratoryMomentAudioZone,
  defaultBedrockReply,
  debugLog,
} from '../constants';
import { useMytaverse } from '../../../../../providers/MytaverseProvider';
import { useMytaverseEvent } from '../../../../dashboard/providers/MytaverseEventProvider';

import {
  getLocalStorageValue,
  setLocalStorageValue,
} from '../../../../../helpers/localStorage';
import { StorageTypes, StorageValues } from '../../../../../interfaces/storage';
import { WebsocketAction } from '../../../../../interfaces/webSocketConnectionInfo';

import { env } from '../../../../../env';

const { REACT_APP_AI_API_URL } = env;

export const PocAiContext = createContext<IPocAiContext>({} as IPocAiContext);

export const usePocAi = () => useContext(PocAiContext);

const socket = io(REACT_APP_AI_API_URL);
let audioContext: any;
let audioInput: any;
let processor: any;
let visualizerInstance: any;
// TODO: temp workaround, do a proper fix on SM side to have an option to use Transcibe only
let activeScreenGlob = POC_SCREENS.DEFAULT;
// TODO: temp workaround
let isMutedGlob = false;
let audioPlayBackType = '';
let currentRegion = '';

export const PocAiProvider: FC<PocAiProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [activeScreen, setActiveScreen] = useState<POC_SCREENS>(
    POC_SCREENS.DEFAULT,
  );
  const [navigationHistory, setNavigationHistory] = useState<POC_SCREENS[]>([
    POC_SCREENS.DEFAULT,
  ]);
  //const [audioPlayBackType, setAudioPlayBackType] = useState<EAudioPlaybackType>();
  let userName = '';

  const [chatHistory, setChatHistory] = useState<IPocMessage[]>([]);

  const [audioPromptHistory, setAudioPromptHistory] = useState<string[]>([]);

  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMutedLoc] = useState(false);

  const [region, setRegion] = useState({
    current: '',
    previous: '',
  });

  const { user, sendJSONMessageToWebSocket } = useMytaverse();
  const mytaverseEventProvider = useMytaverseEvent() || {};

  if (user.firstName) {
    userName = user.firstName;
  } else if (user.name) {
    userName = user.name;
  }

  const setIsMuted = () => {
    setIsMutedLoc(!isMuted);
    isMutedGlob = !isMutedGlob;
  };

  const navigate = (screen: POC_SCREENS, navigateBack?: boolean) => {
    if (navigateBack) {
      activeScreenGlob = navigationHistory[navigationHistory.length - 2];
      setActiveScreen(navigationHistory[navigationHistory.length - 2]);
    } else {
      activeScreenGlob = screen;
      setActiveScreen(screen);
    }

    setNavigationHistory((prevState) => {
      if (navigateBack) {
        prevState.pop();
        return [...prevState];
      }

      return [...prevState, screen];
    });
  };

  const addMessage = useCallback(
    ({ text, type }: { text: string; type: PocMessageType }) => {
      setChatHistory((prevState) => {
        return [...prevState, { text, type, timestamp: Date.now() }];
      });
    },
    [],
  );

  const addAudioHistory = useCallback((text: string) => {
    setAudioPromptHistory((prevState) => {
      return [...prevState, text];
    });
  }, []);

  const handleSendRequest = useCallback(
    async (text: string) => {
      addMessage({ text, type: PocMessageType.USER });
      setAwaitingResponse(true);

      audioPlayBackType = EAudioPlaybackType.CHAT;
      socket.emit('SocketEventRequest', {
        type: 'BedrockEvent',
        data: { text },
      });
    },
    [addMessage],
  );

  const queryBedrockTest = () => {
    debugLog('queryBedrockTest clicked');
    socket.emit('SocketEventRequest', {
      type: 'BedrockEvent',
      data: { text: 'What is AWS?' },
    });
  };

  const generateAudioPlaybackTest = () => {
    debugLog('generateAudioPlaybackTest clicked');
    socket.emit('SocketEventRequest', {
      type: 'PollyEvent',
      data: { text: 'This is a test' },
    });
  };

  const generateGreetingsAudio = (isFirstTimeLogin?: boolean) => {
    audioPlayBackType = EAudioPlaybackType.GREETINGS;
    debugLog('generateGreetingsAudio clicked');
    socket.emit('SocketEventRequest', {
      type: 'PollyEvent',
      data: {
        text: (isFirstTimeLogin
          ? greetingsText
          : greetingsTextSecondary
        ).replace('@userName@', userName ? ', ' + userName : ''),
      },
    });
  };

  const playBackOnFirstTime = () => {
    debugLog('playBackOnFirstTime');

    const assistantStorage = getLocalStorageValue<any>(
      StorageValues.Assistant,
    ) || {
      isFirstTime: true,
    };

    if (assistantStorage.isFirstTime) {
      setOpen(true);

      setLocalStorageValue(StorageValues.Assistant, {
        isFirstTime: false,
      });

      generateGreetingsAudio(true);
    } else {
      // TEMP fix for auditorium exit button issue
      // when assistant 2nd login greetings is replayed
      // TODO: proper fix
      const assistantStorageSession = getLocalStorageValue<any>(
        StorageValues.Assistant,
        StorageTypes.Session,
      ) || {
        isGreetingsPlayed: false,
      };

      if (!assistantStorageSession.isGreetingsPlayed) {
        setOpen(true);

        setLocalStorageValue(
          StorageValues.Assistant,
          {
            ...assistantStorageSession,
            isGreetingsPlayed: true,
          },
          StorageTypes.Session,
        );

        generateGreetingsAudio(false);
      }
    }
  };

  const startRecording = async () => {
    debugLog('start recording audio');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      debugLog('microphone access granted');
      audioContext = new AudioContext();
      audioInput = audioContext.createMediaStreamSource(stream);
      processor = audioContext.createScriptProcessor(1024, 1, 1);
      audioInput.connect(processor);
      processor.connect(audioContext.destination);

      processor.onaudioprocess = (e: any) => {
        const float32Array = e.inputBuffer.getChannelData(0);
        const int16Array = new Int16Array(float32Array.length);
        for (let i = 0; i < float32Array.length; i++) {
          int16Array[i] = Math.max(
            -32768,
            Math.min(32767, Math.floor(float32Array[i] * 32768)),
          );
        }
        debugLog(
          'sending audio chunk to server, size',
          int16Array.buffer.byteLength,
        );
        socket.emit('SocketEventRequest', {
          type: 'TranscribeAudioEvent',
          data: { audio: int16Array.buffer },
        });
      };

      socket.emit('SocketEventRequest', {
        type: 'StartTranscriptionEvent',
        data: { isTranscribeOnly: false },
      });
      debugLog('startTranscription event emitted');
    } catch (error) {
      console.error('error accessing microphone', error);
    }
  };

  function stopRecording() {
    debugLog('stop recording audio');

    if (audioContext && audioContext.state !== 'closed') {
      try {
        audioInput.disconnect();
        processor.disconnect();
        audioContext.close();
        socket.emit('SocketEventRequest', { type: 'StopTranscriptionEvent' });
      } catch (error) {
        console.error('error closing audio', error);
      }
    }
  }

  function stopPlayback() {
    debugLog('stop playback audio');

    try {
      visualizerInstance.stopAudio();
    } catch (error) {}
  }

  function isAIEnabledForEvent() {
    if (
      mytaverseEventProvider &&
      mytaverseEventProvider.currentEventId &&
      mytaverseEventProvider.events.find(
        (x: any) => x.eventId === mytaverseEventProvider.currentEventId,
      )?.enableAssistant
    ) {
      return true;
    }

    return false;
  }

  useEffect(() => {
    debugLog('mytaverseEventProvider', mytaverseEventProvider);
    setRegion({
      previous: region.current,
      current: mytaverseEventProvider.currentRegion?.region || '',
    });
    currentRegion = mytaverseEventProvider.currentRegion?.region || '';

    const assistantStorage = getLocalStorageValue<any>(
      StorageValues.Assistant,
      StorageTypes.Session,
    ) || {
      audioZones: [],
    };

    if (assistantStorage && !assistantStorage.hasOwnProperty('audioZones')) {
      assistantStorage.audioZones = [];
    }

    if (
      mytaverseEventProvider.currentRegion?.region &&
      audioZoneScript[mytaverseEventProvider.currentRegion?.region] &&
      assistantStorage.audioZones.indexOf(
        mytaverseEventProvider.currentRegion?.region,
      ) === -1
    ) {
      audioPlayBackType = EAudioPlaybackType.AUDIO_ZONE;
      assistantStorage.audioZones.push(
        mytaverseEventProvider.currentRegion?.region,
      );
      setLocalStorageValue(
        StorageValues.Assistant,
        assistantStorage,
        StorageTypes.Session,
      );

      debugLog(
        'generate audio for audio zone',
        mytaverseEventProvider.currentRegion?.region,
      );
      debugLog(
        'audio zone script',
        audioZoneScript[mytaverseEventProvider.currentRegion?.region],
      );

      if (!open) {
        setOpen(true);
      }

      socket.emit('SocketEventRequest', {
        type: 'PollyEvent',
        data: {
          text: audioZoneScript[
            mytaverseEventProvider.currentRegion?.region
          ].replace('@userName@', userName ? ', ' + userName : ''),
        },
      });
    }
  }, [mytaverseEventProvider.currentRegion]);

  // simulate N key in GameCast
  function testKeyboard() {
    const enterEvent = new KeyboardEvent('keydown', keys.teleport);
    mytaverseEventProvider.gamecastClientRef.current.detachInput();
    mytaverseEventProvider.gamecastClientRef.current.attachInput();
    mytaverseEventProvider.gamecastClientRef.current.processKeyboardEvent(
      enterEvent,
    );
  }

  useEffect(() => {
    console.warn('region has changed', region);
  }, [region]);

  function audioEndCallback() {
    debugLog('audioEndCallback', { audioPlayBackType, currentRegion });

    if (currentRegion === celebratoryMomentAudioZone) {
      debugLog('trigger celebratory moment websocket message');

      const wsMessage = {
        action: WebsocketAction.MytaverseAction,
        tag: 'MytaEvent.Action.CelebrationMoment.Start',
        data: '',
      };
      debugLog('sendJSONMessageToWebSocket', wsMessage);

      sendJSONMessageToWebSocket(wsMessage);
    }

    if (audioPlayBackType === EAudioPlaybackType.AUDIO_ZONE) {
      setOpen(false);
    }

    if (audioPlayBackType === EAudioPlaybackType.GREETINGS) {
      const assistantStorage = getLocalStorageValue<any>(
        StorageValues.Assistant,
      ) || {
        isFirstTime: true,
      };

      if (!assistantStorage.isFirstTime) {
        setOpen(false);
      }
    }
  }

  function teleportToRegion(regionName: string) {
    debugLog('teleport region', regionName);

    const wsMessage = {
      action: WebsocketAction.MytaverseAction,
      tag: 'MytaEvent.Action.Teleport.ToRegion',
      data: regionName,
    };
    debugLog('sendJSONMessageToWebSocket', wsMessage);

    sendJSONMessageToWebSocket(wsMessage);
  }

  function sendWSMessage(message: string) {
    debugLog('sendWSMessage', message);

    const wsMessage = JSON.parse(message);

    debugLog('sendJSONMessageToWebSocket', wsMessage);

    sendJSONMessageToWebSocket(wsMessage);
  }

  useEffect(() => {
    function onConnect() {
      debugLog('connected to AI POC');
      setIsConnected(true);
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    function onQueryBedrock(data: any) {
      debugLog('activeScreen', activeScreenGlob);

      if (
        activeScreenGlob !== POC_SCREENS.DEFAULT &&
        activeScreenGlob !== POC_SCREENS.TELEPORT
      ) {
        debugLog('onQueryBedrock', data);
        setAwaitingResponse(false);
        socket.emit('SocketEventRequest', {
          type: 'PollyEvent',
          data: {
            text: data,
          },
        });
        //socket.emit('generateAudio', { text: data });
        addMessage({ text: data, type: PocMessageType.ASSISTANT });
      }
    }

    function onGenerateAudio(data: any) {
      debugLog('activeScreen', activeScreenGlob);
      debugLog('isMutedGlob', isMutedGlob);

      if (/*activeScreenGlob !== POC_SCREENS.DEFAULT &&*/ !isMutedGlob) {
        debugLog('onGenerateAudio data len', data.length);

        if (visualizerInstance) {
          visualizerInstance.stopAudio();
        }
        visualizerInstance = visualizeAudio(
          assistantSize.width,
          assistantSize.height,
          audioEndCallback,
        );
        visualizerInstance.initialize(data);
      }
    }

    function onTranscription(data: any) {
      debugLog('onTranscription', data);

      if (data.isFinal) {
        stopRecording();
        setIsRecording(false);

        if (activeScreenGlob === POC_SCREENS.DEFAULT) {
          const actionsTranscribePromptResult = processActionsAudioPrompt(
            data.text.toLowerCase(),
          );

          debugLog('actions prompt', {
            prompt: data.text,
            result: actionsTranscribePromptResult,
          });

          if (actionsTranscribePromptResult) {
            audioPlayBackType = EAudioPlaybackType.VOICE_COMMAND;

            if (actionsTranscribePromptResult.indexOf('expression') > -1) {
              const enterEvent = new KeyboardEvent(
                'keydown',
                keys[actionsTranscribePromptResult],
              );
              mytaverseEventProvider.gamecastClientRef.current.detachInput();
              mytaverseEventProvider.gamecastClientRef.current.attachInput();
              mytaverseEventProvider.gamecastClientRef.current.processKeyboardEvent(
                enterEvent,
              );
            } else if (actionsTranscribePromptResult.indexOf('teleport') > -1) {
              for (let i = 0; i < teleportPlaces.length; i++) {
                if (
                  actionsTranscribePromptResult.indexOf(teleportPlaces[i]) > -1
                ) {
                  const teleportRegion = teleportPlaces[i]
                    .replace('second', '2nd')
                    .replace('third', '3rd');

                  teleportToRegion(teleportRegion);
                  break;
                }
              }
            }

            addAudioHistory(data.text);
          } else {
            // playback default audio reply
            socket.emit('SocketEventRequest', {
              type: 'PollyEvent',
              data: {
                text: defaultBedrockReply,
              },
            });
            navigate(POC_SCREENS.TELEPORT);
          }
        } else {
          addMessage({ text: data.text, type: PocMessageType.USER });
        }
      }
    }

    function processActionsAudioPrompt(text: string) {
      const actionTypes = [
        actionsPromptOptionsExpression,
        actionsPromptOptionsTeleport,
      ];
      let match = '';

      for (let a = 0; a < actionTypes.length; a++) {
        const actions = actionTypes[a];

        for (let i = 0; i < actions.length; i++) {
          const partial = actions[i].split(' ');
          let occurencies = [];

          // debugLog(partial);

          for (let j = 0; j < partial.length; j++) {
            if (text.indexOf(partial[j]) > -1) {
              occurencies.push(partial[j]);
            }
          }

          if (occurencies.join(' ') === actions[i]) {
            debugLog('match!', actions[i], text);
            match = actions[i];
          }
        }
      }

      return match;
    }

    const socketEventHandler = (msg: any) => {
      debugLog('SocketEventResponse', msg);

      switch (msg.type) {
        case 'BedrockEvent':
          onQueryBedrock(msg.data.text);
          break;
        case 'PollyEvent':
          onGenerateAudio(msg.data.audio);
          break;
        case 'ErrorEvent':
          console.error('Server error:', msg.data.message);
          break;
        case 'TranscriptionEvent':
          onTranscription(msg.data);
          break;
      }
    };

    socket.on('SocketEventResponse', socketEventHandler);

    return () => {
      socket.off('SocketEventResponse', socketEventHandler);
    };
  }, []);

  return (
    <PocAiContext.Provider
      value={{
        activeScreen,
        setActiveScreen,
        open,
        setOpen,
        handleSendRequest,
        chatHistory,
        queryBedrockTest,
        generateAudioPlaybackTest,
        generateGreetingsAudio,
        playBackOnFirstTime,
        startRecording,
        stopRecording,
        navigate,
        setNavigationHistory,
        awaitingResponse,
        stopPlayback,
        isAIEnabledForEvent,
        isRecording,
        setIsRecording,
        audioPromptHistory,
        testKeyboard,
        isMuted,
        setIsMuted,
        teleportToRegion,
        sendWSMessage,
      }}
    >
      {children}
    </PocAiContext.Provider>
  );
};
